import { MergeFieldFormInputTypes } from '@app/enums';
import { Model } from '@app/interfaces';
import { MergeFieldName } from '@app/modules/documents-v2/services/signing-template.service';

export interface MergeFieldDropdownInputAttributes {
    options: Array<string | Model>;
    optionLabel?: string;
    showAddButton: boolean;
    addButtonLabel?: string;
}

export interface MergeFieldFormInputAttributes {
    label: string;
    inputType: MergeFieldFormInputTypes | null;
    required: boolean;
    attributeName: string;
    dropDownOptions?: MergeFieldDropdownInputAttributes;
}

export const EXPIRES_ON_DATE_FIELD = 'Candidate - Offer Letter Expiration Date';

const CANDIDATE_DATE_FIELDS = ['Candidate - Start Date', 'Candidate - Contract End Date', EXPIRES_ON_DATE_FIELD];

const CANDIDATE_DROPDOWN_FIELDS = [
    'Candidate - Position',
    'Candidate - Department',
    'Candidate - Office Location',
    'Candidate - Employment Type',
    'Candidate - Compensation Frequency',
];

const CANDIDATE_TEXT_FIELDS = [
    'Candidate - Stock Options',
    'Candidate - Contract Term',
    'Candidate - Address',
    'Candidate - Probation Period',
];

const CANDIDATE_NUMBER_FIELDS = [
    'Candidate - Hours Per Week',
    'Candidate - Vacation Days',
    'Candidate - Compensation Rate',
];

const OFFER_LETTER_ATTRIBUTE_NAMES = {
    'Candidate - Compensation Frequency': 'compensationFrequency',
    'Candidate - Compensation Rate': 'compensationRate',
    'Candidate - Probation Period': 'probationPeriod',
    'Candidate - Contract Term': 'contractTerm',
    'Candidate - Address': 'candidateAddress',
    'Candidate - Hours Per Week': 'hoursPerWeek',
    'Candidate - Vacation Days': 'vacationDays',
    'Candidate - Stock Options': 'stockOptions',
    'Candidate - Position': 'position',
    'Candidate - Department': 'department',
    'Candidate - Office Location': 'location',
    'Candidate - Employment Type': 'employmentType',
    'Candidate - Start Date': 'firstDayOfWorkOn',
    'Candidate - Contract End Date': 'contractEndsOn',
    'Candidate - Offer Letter Expiration Date': 'expiresOn',
};

export const buildOfferLetterFieldAttributes = (mergeField: MergeFieldName): MergeFieldFormInputAttributes => {
    return {
        label: getFieldLabel(mergeField),
        inputType: getInputTypeFromMergeField(mergeField),
        required: true,
        attributeName: getAttributeName(mergeField),
    };
};

export function mergeFieldFormInputAttributesContainsExpiresOn(mergeFieldNames: string[]): boolean {
    return mergeFieldNames.some((name) => name === EXPIRES_ON_DATE_FIELD);
}

function getInputTypeFromMergeField(mergeField: MergeFieldName): MergeFieldFormInputTypes | null {
    if (isDateField(mergeField)) {
        return MergeFieldFormInputTypes.date;
    }

    if (isDropdownField(mergeField)) {
        return MergeFieldFormInputTypes.dropdown;
    }

    if (isTextField(mergeField)) {
        return MergeFieldFormInputTypes.text;
    }

    if (isNumberField(mergeField)) {
        return MergeFieldFormInputTypes.number;
    }

    return null;
}

/**
 * Strips out fields from HelloSign that
 * we do not need to handle
 */
export function displayMergeField(mergeField: MergeFieldName): boolean {
    return (
        isDateField(mergeField) || isDropdownField(mergeField) || isTextField(mergeField) || isNumberField(mergeField)
    );
}

function getFieldLabel(fieldName: string): string {
    return fieldName.replace('Candidate - ', 'offer-letter.merge-fields.').replaceAll(' ', '-').toLowerCase(); // snake-case-ify for translation keys
}

function getAttributeName(fieldName: string): string {
    return OFFER_LETTER_ATTRIBUTE_NAMES[fieldName];
}

function isDateField(mergeField: MergeFieldName): boolean {
    return CANDIDATE_DATE_FIELDS.includes(mergeField);
}

function isDropdownField(mergeField: MergeFieldName): boolean {
    return CANDIDATE_DROPDOWN_FIELDS.includes(mergeField);
}

function isTextField(mergeField: MergeFieldName): boolean {
    return CANDIDATE_TEXT_FIELDS.includes(mergeField);
}

function isNumberField(mergeField: MergeFieldName): boolean {
    return CANDIDATE_NUMBER_FIELDS.includes(mergeField);
}
